import {
  ReactElement,
  useCallback,
  FC,
  useEffect,
  useState,
  createRef,
} from 'react'
import { useEffectOnce } from 'usehooks-ts'
import styled from 'styled-components'
import StarRatings from 'react-star-ratings'
import { getDatabase, ref, onValue, runTransaction } from 'firebase/database'
import { AiFillStar } from 'react-icons/ai'
import { FaRegHandPointer } from 'react-icons/fa'

interface UserVoteProps {
  tenant?: object
  colorMode?: string
  audiencerating?: string
  tippId: string
  audienceRateCount?: number
  handleUpdateRatingCount: (rating: number) => void
}

const AudienceRating: FC<UserVoteProps> = ({
  tenant,
  colorMode,
  audiencerating,
  tippId,
  audienceRateCount,
  handleUpdateRatingCount,
}): ReactElement => {
  const [isMobile, setIsMobile] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [userRating, setUserRating] = useState<number | undefined>(-1)
  const [userChoice, setUserChoice] = useState<number | undefined>(-1)
  const [userIsRating, setUserIsRating] = useState(false)
  const [audienceRating, setAudienceRating] = useState(0)
  const ratingRef = createRef<HTMLDivElement>()
  const [wrapperWidth, setWrapperWidth] = useState(0)
  const popRef = createRef<HTMLDivElement>()
  const [popWidth, setPopWidth] = useState(999999)

  const handleAddVote = () => {
    const db = getDatabase()
    setIsSubmitting(true)
    localStorage.setItem(tippId, userChoice ? userChoice.toString() : '0')
    // console.log('Add vote for', tippId, 'with value', userChoice)

    const postRef = ref(db, 'votes/' + tippId + '/' + userChoice + /votes/)

    runTransaction(postRef, (post) => {
      if (post) {
        post += 1
      } else {
        post = 1
      }
      return post
    })
  }

  const handleRate = (newRating: number) => {
    setUserIsRating(true)
    setUserChoice(newRating)
  }

  const handleCancelRate = () => {
    setUserIsRating(false)
  }

  const popContent = !isSubmitting ? (
    <ConfirmContainer
      onClick={handleCancelRate}
      style={{ left: wrapperWidth / 2 - popWidth / 2 + 'px' }}
      ref={popRef}
    >
      <div>Ihre Wertung:</div>
      <div className='choice'>
        {new Array(userChoice && userChoice > 0 ? userChoice : 0)
          .fill(0)
          .map((s, i) => (
            <AiFillStar key={`start-${i}`} />
          ))}
      </div>
      <div>
        <div className='sure'>übermitteln?</div>
        <ButtonsContainer>
          <Button onClick={handleAddVote}>
            <div>Ja</div>
          </Button>
          <Button onClick={handleCancelRate}>
            <div>Nein</div>
          </Button>
        </ButtonsContainer>
      </div>
    </ConfirmContainer>
  ) : (
    <div className='confirm-wrapper'>
      <div className='ui segment'>
        <div className='ui active inverted dimmer' />
        <p />
      </div>
    </div>
  )

  useEffect(() => {
    const db = getDatabase()
    const votes = ref(db, 'votes/' + tippId)

    onValue(votes, (snapshot) => {
      const oneStarVotes = snapshot.child('1/votes').val()
      const twoStarVotes = snapshot.child('2/votes').val()
      const threeStarVotes = snapshot.child('3/votes').val()
      const fourStarVotes = snapshot.child('4/votes').val()
      const fiveStarVotes = snapshot.child('5/votes').val()

      const totalStars =
        oneStarVotes +
        twoStarVotes * 2 +
        threeStarVotes * 3 +
        fourStarVotes * 4 +
        fiveStarVotes * 5
      const totalVotes =
        oneStarVotes +
        twoStarVotes +
        threeStarVotes +
        fourStarVotes +
        fiveStarVotes
      const averageVote = totalStars / totalVotes

      setAudienceRating(averageVote)
      setIsSubmitting(false)
      setUserIsRating(false)

      handleUpdateRatingCount(totalVotes)
      // console.log('Set new value', tippId)
      const storedRating = localStorage.getItem(tippId)

      if (storedRating) {
        setUserRating(parseInt(storedRating))
      }
    })
  }, [handleUpdateRatingCount, tippId])

  useEffect(() => {
    if (ratingRef.current !== null) {
      setWrapperWidth(ratingRef.current.offsetWidth)
    }
    if (popRef.current !== null) {
      setPopWidth(popRef.current.offsetWidth)
    }
  }, [wrapperWidth, popWidth, popRef, ratingRef])

  return (
    <Audiencerating ref={ratingRef} className={isMobile ? 'mobile' : 'desktop'}>
      {audienceRateCount !== undefined && audienceRateCount > 0 && (
        <>
          <h4>Leserbewertung</h4>
          <div className='average'>
            <span>ø</span> {(Math.round(audienceRating * 2) / 2).toFixed(1)}
          </div>
        </>
      )}

      {userRating === -1 && (
        <>
          {audienceRateCount !== undefined && audienceRateCount > 0 && (
            <div className='ratecount'>
              ({audienceRateCount}{' '}
              {audienceRateCount > 1 ? 'Bewertungen' : 'Bewertung'})
            </div>
          )}
          <div className='callto'>Wie gefällt Ihnen dieses Buch?</div>
        </>
      )}
      <StarRatings
        rating={
          userIsRating
            ? userChoice
            : userRating === undefined
            ? audienceRating
              ? parseFloat((Math.round(audienceRating * 2) / 2).toFixed(1)) // round to nearest .5
              : 0
            : audienceRating
            ? parseFloat((Math.round(audienceRating * 2) / 2).toFixed(1)) // round to nearest .5
            : 0
        }
        starRatedColor='#2ca29f'
        starEmptyColor={colorMode === 'dark' ? '#555' : '#ddd'}
        svgIconPath='M24 1.175l7.553 14.84L48 18.611 36.22 30.38l2.613 16.444L24 39.26 9.167 46.825 11.78 30.38 0 18.613l16.447-2.598z'
        starDimension='20px'
        starSpacing='3px'
        starHoverColor='#2ca29f'
        changeRating={userRating === -1 ? handleRate : undefined}
        numberOfStars={5}
        name='rating'
      />
      {userRating === -1 && (
        <div className='handicon'>
          <FaRegHandPointer />
        </div>
      )}
      {userRating !== -1 && audienceRateCount && (
        <div className='ratecount'>
          ({audienceRateCount}{' '}
          {audienceRateCount > 1 ? 'Bewertungen' : 'Bewertung'})
        </div>
      )}
      {userIsRating && popContent}
    </Audiencerating>
  )
}
const Audiencerating = styled.div`
  margin-top: 2rem;
  color: var(--text-color);
  position: relative;
  text-align: center;

  h4 {
    margin-bottom: 0;
  }
  .average {
    font-size: 2.3rem;
    font-family: var(--font-serif);
    left: -6px;
    position: relative;
    span {
      font-size: 2.1rem;
    }
  }
  .ratecount {
    font-size: 1.4rem;
    color: var(--gray-3);
  }
  .callto {
    font-size: 1.7rem;
    font-weight: 600;
    margin-top: 0.5rem;
  }
  .handicon {
    font-size: 2.5rem;
  }
`

const ConfirmContainer = styled.div`
  background-color: var(--confirm-bg);
  font-size: 1.55rem;
  position: absolute;
  bottom: 40px;
  font-family: var(--font-sans-serif);
  background: ${({ theme }) => theme.coloredBackground};
  box-shadow: 0 0 10px 0 var(--confirm-shadow);
  padding: 18px;

  .choice {
    font-size: 2rem;
    color: var(--petrol-main);
  }
`

const ButtonsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 10px;
  margin-top: 0.4rem;
`

const Button = styled.div`
  display: inline-block;
  color: var(--gray-6);
  background: var(--brandblue-main);
  padding: 3px 20px;
  &:hover {
    cursor: pointer;
    background: var(--gray-2);
`

export default AudienceRating
